import React from 'react'
import tw from 'tailwind-styled-components'
import {AnyColor, colorBgClass} from '@components/common/base/design-system'

export interface Props {
  value: number
  maxValue: number
  className?: string
  percent?: boolean
  bgColor?: AnyColor
  lineColor?: AnyColor
  textPosition?: 'left' | 'right' | 'none'
  size?: '4' | '8'
}

const TwContainer = tw.div`
  flex
  items-center
  gap-12
  text-secondary
`

const TwLine = tw.div<{$bgColor: AnyColor; $size: '8' | '4'}>`
  relative
  w-full
  rounded-4
  overflow-hidden

  ${p => (p.$size === '8' ? 'h-8' : 'h-4')}
  ${p => colorBgClass(p.$bgColor)}
`

const TwLineColored = tw.div<{
  $completed?: boolean
  $bgColor?: AnyColor
  $size: '8' | '4'
}>`
  absolute
  left-0
  top-0
  rounded-4
  w-full
  min-w-8
  z-10
  transition-all
  duration-300

  ${p => (p.$size === '8' ? 'h-8' : 'h-4')}

  ${p => {
    if (p.$bgColor) {
      return `bg-${p.$bgColor}`
    }

    return p.$completed ? 'bg-status-success' : 'bg-icon-placeholder'
  }}
`

const TwText = tw.div`
  text-12
  leading-4
  min-w-32
`

const getText = (value: number, maxValue: number, percent: boolean) => {
  if (percent) {
    return `${Math.floor((value / maxValue) * 100)}%`
  }

  return `${value}/${maxValue}`
}

export const ProgressBar = ({
  value,
  maxValue,
  percent = false,
  className,
  bgColor = 'background-secondary',
  lineColor,
  textPosition = 'right',
  size = '8'
}: Props) => {
  value = Math.min(value, maxValue)
  const text = getText(value, maxValue, percent)
  const completed = maxValue === value

  const calculatedOffset = Math.round((100 * value) / maxValue) - 100
  const percentOffset = isNaN(calculatedOffset) ? -100 : calculatedOffset

  return (
    <TwContainer className={className} data-test="ProgressBar">
      {textPosition === 'left' && <TwText>{text}</TwText>}
      <TwLine $bgColor={bgColor} $size={size} data-test="ProgressLine">
        <TwLineColored
          data-test="ProgressLineColored"
          $completed={completed}
          $bgColor={lineColor}
          style={{transform: `translateX(${percentOffset}%)`}}
          $size={size}
        />
      </TwLine>
      {textPosition === 'right' && <TwText>{text}</TwText>}
    </TwContainer>
  )
}
